import { ROUTE_CACHE_CAP } from './block-jam-save-data';
import { AVATAR_ID_PATTERN, avatarStateFromRaw } from '../meta/avatars';
import { MILESTONE_COUNTER_CAP, isMilestoneKey, milestoneDef, } from '../meta/milestones';
import { missionStateFromRaw } from '../meta/missions';
import { winStreakFromRaw } from '../meta/win-streak';
import { DAILY_LOGIN_SCHEDULE, QUESTS } from '../constants/economy';
import { TRACK_IDS } from '../config/generation-config';
/**
 * The largest level number a save may name (Module 50, WP-50.6 — AUD-021).
 *
 * This bound is not decoration. Generating level N replays the anti-repetition
 * recipe history for every level before it (`buildHistoryBefore` /
 * `RecipeChain.historyBefore` in `generator/level-recipe.ts`), so the cost of
 * *booting into* a level is linear in the level number and nothing in the
 * generator caps it. Measured on the shipped config, cold, one `generate(N)` on
 * a fresh generator — which is exactly what `initialize()` does when a save
 * names level N:
 *
 * | level     |    time |
 * | --------- | ------- |
 * | 1,000     |   125ms |
 * | 20,000    |  1.8s   |
 * | 200,000   | 18.1s   |
 * | 999,999   | 89.2s   |
 *
 * Above ~5,000 it is a straight line at ~0.09ms per level of history. The
 * audit's `levelId: 999999` case "never renders" for this reason and no other:
 * a minute and a half of blocked main thread before a canvas can exist. (WP-36.11
 * already noted the replay goes superlinear in the Infinite band; this is the far
 * end of that same curve.)
 *
 * 10,000 is chosen from the two costs, which are not symmetric:
 *
 *  - **Rejecting a level a real player reached** throws away their entire
 *    progress. Unrecoverable, so the bound must sit far above anything
 *    reachable — at even one minute per level, 10,000 levels is a week of
 *    continuous play, and WP-50.5 measured real levels taking real drags.
 *  - **Accepting a tampered level below the bound** costs a slow boot. At
 *    10,000 the replay is ~0.9s: bad, and still three orders of magnitude off
 *    the 89s that made AUD-021 a finding.
 *
 * If the replay is ever made non-linear, this bound can move; it is a
 * consequence of that implementation, not a game-design statement about how
 * long Block Jam is.
 */
export const MAX_LEVEL_ID = 10000;
/**
 * The largest ORDINAL a save may name (Module 65, WP-65.B1).
 *
 * Much larger than `MAX_LEVEL_ID`, and for a reason worth stating: the bound
 * above exists because booting into level N replayed the recipe history of every
 * level before it, so cost was linear in the level number. **Tracks remove that
 * cost.** The effective level is capped by the track's `lastLevel` — at most 120
 * — so booting into puzzle 100,000 builds exactly one board at level 120 and
 * replays at most 119 recipes. The ordinal is not on that curve at all.
 *
 * So this bound is not about time. It is about a hand-edited save naming an
 * ordinal so large that the arithmetic behind `variant` stops being meaningful.
 * A million puzzles is far past any real player (WP-50.5 measured real levels
 * taking real drags) and far short of anywhere the seed maths degrades.
 *
 * `MAX_LEVEL_ID` is kept and still exported: it bounds the level a *board* may
 * be generated at, which the Studio and the QA jump still exercise.
 */
export const MAX_ORDINAL = 1000000;
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Strings only, in order, deduped — anything else in the array is dropped. */
function stringList(value) {
    if (!Array.isArray(value))
        return undefined;
    return [...new Set(value.filter((entry) => typeof entry === 'string'))];
}
function boolean(value) {
    return typeof value === 'boolean' ? value : undefined;
}
/**
 * The Block Jam save, validated at the load boundary (Module 50, WP-50.6 —
 * closes AUD-021).
 *
 * `null` means "there is no usable save", which is the path corrupt JSON has
 * always taken: `initialize()` reads `saved?.levelId ?? 1` and starts a new
 * game. That is reserved for `levelId`, because `levelId` is the only field
 * whose loss is the save's loss — everything else is a "have you seen this yet"
 * marker, and dropping a player's whole progress because someone edited
 * `seenUnlocks` would be a worse bug than the one this fixes. Those fields are
 * sanitised to their absent-field defaults instead.
 *
 * Only fields the game READS are checked. `boardState` and `hintsUsed` are
 * written by `_commitState` and never read back, so they are passed through
 * untouched. Module 129 (WP-129.3) ended "boards are regenerated, never
 * restored" for a puzzle in progress: that board now lives in each track's
 * `board` record, enveloped here and fitted to the rebuilt puzzle by
 * `resumeTrackBoard` (which also bounds the budget and the rescue counters).
 *
 * ## Module 50 (WP-50.28a) — the move budget is NOT validated here, deliberately
 *
 * WP-50.6 handed this function two fields to bound: `moveBudget` (a hand-edited
 * one is an infinite-moves cheat) and `movesAdsUsed` (an unbounded ad
 * allowance). They were built, and the attack turns out not to exist: both live
 * **inside `boardState`**, and `initialize()` reads `saved?.levelId` and then
 * regenerates the board — so an edited budget is overwritten by
 * `_adoptLevel`'s recomputed one before the first frame, on every load.
 *
 * Validating them anyway would be a check that can never fire: dead code the
 * next reader has to decode before concluding it does nothing. The bound that
 * matters is the one already above — `levelId`, the only field this save is
 * actually read for. If `boardState` ever starts being restored, these two are
 * the first fields that need bounds, and they are *repair* cases (clamp to the
 * level's real budget and to the 3-ad cap), never *reject* ones.
 */
/**
 * One track's progress, repaired rather than rejected.
 *
 * An absent or nonsense entry becomes a fresh track (ordinal 1, nothing paid).
 * Losing one track's position is not the save's loss — the other two and the
 * wallet are untouched — so the rejection rule below is reserved for the shape
 * of the save itself.
 */
function trackProgress(raw) {
    const fresh = { ordinal: 1, maxPaidOrdinal: 0 };
    if (!isPlainObject(raw))
        return fresh;
    const ordinal = typeof raw.ordinal === 'number' &&
        Number.isInteger(raw.ordinal) &&
        raw.ordinal >= 1 &&
        raw.ordinal <= MAX_ORDINAL
        ? raw.ordinal
        : 1;
    // WP-50.25's reasoning, carried over: an out-of-range value falls back to 0,
    // which RE-PAYS puzzles rather than under-paying them. Lowering it is the
    // direction that re-opens the faucet, and the server allowlist — not this
    // function — is what actually bounds any single credit.
    const maxPaidOrdinal = typeof raw.maxPaidOrdinal === 'number' &&
        Number.isInteger(raw.maxPaidOrdinal) &&
        raw.maxPaidOrdinal >= 0 &&
        raw.maxPaidOrdinal <= MAX_ORDINAL
        ? raw.maxPaidOrdinal
        : 0;
    // WP-65.F2 — a REPAIR like the fields above: keep the entries that are sane
    // non-negative integers and drop the rest. Dropping an entry re-grants a
    // free use, which costs the house one Surge on one track; rejecting the save
    // would cost the player their progress.
    let powerupFreeUsed;
    if (isPlainObject(raw.powerupFreeUsed)) {
        powerupFreeUsed = {};
        for (const [id, n] of Object.entries(raw.powerupFreeUsed)) {
            if (typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 1000)
                powerupFreeUsed[id] = n;
        }
    }
    // Module 73 (WP-73.7) — the same repair for the free-try glow's counts. A
    // dropped entry only lets a glow show again, which costs nothing.
    let powerupHintsShown;
    if (isPlainObject(raw.powerupHintsShown)) {
        powerupHintsShown = {};
        for (const [id, n] of Object.entries(raw.powerupHintsShown)) {
            if (typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 1000)
                powerupHintsShown[id] = n;
        }
    }
    // Module 77 (D6) — the move-up offer counter. A bad value drops to absent,
    // which can at worst re-show an offer; it never blocks a track.
    const moveUpOffers = typeof raw.moveUpOffers === 'number' &&
        Number.isInteger(raw.moveUpOffers) &&
        raw.moveUpOffers >= 0 &&
        raw.moveUpOffers <= 10
        ? raw.moveUpOffers
        : undefined;
    // Module 80 (WP-80.16/18) — repaired, never rejected: a bad attempt record
    // drops to absent (the counters restart at zero, which only delays help),
    // and a bad skip count drops to absent (a skip may then count as solved).
    const count = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10000;
    const attempt = isPlainObject(raw.attempt) &&
        count(raw.attempt.ordinal) &&
        count(raw.attempt.outOfMoves) &&
        count(raw.attempt.restarts)
        ? {
            ordinal: raw.attempt.ordinal,
            outOfMoves: raw.attempt.outOfMoves,
            restarts: raw.attempt.restarts,
            // Module 81 — rebuilt field by field like the rest, so a counter not
            // listed here would be dropped on every load and the per-puzzle hint
            // cap would reset with a reload. A bad value drops to absent (0).
            ...(count(raw.attempt.highlights)
                ? { highlights: raw.attempt.highlights }
                : {}),
            // Module 129 — only `true` is kept; anything else reads as not revealed.
            ...(raw.attempt.revealed === true ? { revealed: true } : {}),
        }
        : undefined;
    // Module 129 (WP-129.3) — the puzzle in progress. Only the envelope is
    // checked here; `resumeTrackBoard` checks the board against the rebuilt
    // puzzle and starts fresh on any mismatch. A bad envelope drops to absent,
    // which only restarts one puzzle.
    let board;
    if (isPlainObject(raw.board) && count(raw.board.ordinal) && isPlainObject(raw.board.state)) {
        let powerupsUsed;
        if (isPlainObject(raw.board.powerupsUsed)) {
            powerupsUsed = {};
            for (const [id, n] of Object.entries(raw.board.powerupsUsed)) {
                if (typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 1000)
                    powerupsUsed[id] = n;
            }
        }
        board = {
            ordinal: raw.board.ordinal,
            state: raw.board.state,
            ...(powerupsUsed !== undefined ? { powerupsUsed } : {}),
        };
    }
    const skipped = count(raw.skipped) ? raw.skipped : undefined;
    const revealed = count(raw.revealed) ? raw.revealed : undefined;
    return {
        ordinal,
        maxPaidOrdinal,
        ...(attempt !== undefined ? { attempt } : {}),
        ...(board !== undefined ? { board } : {}),
        ...(skipped !== undefined ? { skipped } : {}),
        ...(revealed !== undefined ? { revealed } : {}),
        ...(moveUpOffers !== undefined ? { moveUpOffers } : {}),
        ...(powerupFreeUsed !== undefined ? { powerupFreeUsed } : {}),
        ...(powerupHintsShown !== undefined ? { powerupHintsShown } : {}),
    };
}
/**
 * Module 67 (WP-67.C2) — the daily record, repaired rather than rejected.
 *
 * Anything malformed drops the whole record (absent = "never opened a daily"),
 * EXCEPT the streak pair, which is worth salvaging on its own: losing an
 * attempt state costs one day's record, losing a 40-day streak costs the
 * thing the feature runs on. The date format is checked strictly because it
 * is an equality key — `2026-9-5` and `2026-09-05` must never both exist.
 *
 * The reward, note, is NOT guarded here: the server's wallet stamp
 * (migration 0015) is the once-per-day authority, so a tampered `daily`
 * record can at worst re-play a board for zero coins.
 */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function dailyProgress(raw) {
    if (!isPlainObject(raw))
        return undefined;
    if (typeof raw.date !== 'string' || !DATE_RE.test(raw.date))
        return undefined;
    const state = raw.state === 'in-progress' || raw.state === 'cleared' || raw.state === 'failed'
        ? raw.state
        : undefined;
    if (!state)
        return undefined;
    const streak = typeof raw.streak === 'number' &&
        Number.isInteger(raw.streak) &&
        raw.streak >= 0 &&
        raw.streak <= 100000
        ? raw.streak
        : 0;
    const lastClearDate = typeof raw.lastClearDate === 'string' && DATE_RE.test(raw.lastClearDate)
        ? raw.lastClearDate
        : undefined;
    // Module 79 (WP-79.7) — a bad result is dropped, never the record: the
    // share then says "Cleared" without the moves line.
    const r = raw.result;
    const count = (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 10000;
    const result = isPlainObject(r) && count(r.movesUsed) && count(r.budget) && r.budget > 0
        ? { movesUsed: r.movesUsed, budget: r.budget }
        : undefined;
    return {
        date: raw.date,
        state,
        streak,
        ...(lastClearDate !== undefined ? { lastClearDate } : {}),
        ...(result !== undefined ? { result } : {}),
        ...(raw.shared === true ? { shared: true } : {}),
        ...(raw.retried === true ? { retried: true } : {}),
        // Like the track `boardState`: shape-checked only at the plain-object
        // level; the plugin regenerates from the date's seed when it is unusable.
        ...(isPlainObject(raw.boardState)
            ? { boardState: raw.boardState }
            : {}),
    };
}
export function validateBlockJamSave(raw) {
    if (!isPlainObject(raw))
        return null;
    // Module 65 (WP-65.B1) — the version is now a REJECTION, which it never was
    // before. A v1 save names `levelId` and cannot say which track that progress
    // belonged to, so there is no honest migration; D7 records that this costs
    // nothing because the game has never launched. Returning null is the path
    // corrupt JSON has always taken and it starts a new game.
    if (raw.version !== 2)
        return null;
    const tracks = {};
    for (const id of TRACK_IDS) {
        tracks[id] = trackProgress(isPlainObject(raw.tracks) ? raw.tracks[id] : undefined);
    }
    // `null` is a real value here, not a failure: it means the player has not
    // chosen a track and should be shown the home screen. Anything unrecognised
    // collapses to it, which is the safe direction — the worst case is one extra
    // tap, against silently dropping somebody into a track they did not pick.
    const currentTrack = typeof raw.currentTrack === 'string' &&
        TRACK_IDS.includes(raw.currentTrack)
        ? raw.currentTrack
        : null;
    const seenUnlocks = stringList(raw.seenUnlocks);
    const seenTutorial = boolean(raw.seenTutorial);
    const seenTutorials = stringList(raw.seenTutorials);
    const seenGoalIntro = boolean(raw.seenGoalIntro);
    const seenLanguageIntro = boolean(raw.seenLanguageIntro);
    const seenPoweredHelp = boolean(raw.seenPoweredHelp);
    const seenHomeTour = boolean(raw.seenHomeTour);
    // Module 116 (WP-116.1) — named here or the validator drops it on load.
    const homeOpened = boolean(raw.homeOpened);
    // Module 147 — named here or the validator drops it on load.
    const seenHomeCoach = boolean(raw.seenHomeCoach);
    const settingsOpened = boolean(raw.settingsOpened);
    const languageLineAnswered = boolean(raw.languageLineAnswered);
    // Module 123 — named here or the validator drops it on load.
    const freeCoinsReadyAt = typeof raw.freeCoinsReadyAt === 'number' &&
        Number.isFinite(raw.freeCoinsReadyAt) &&
        raw.freeCoinsReadyAt > 0
        ? raw.freeCoinsReadyAt
        : undefined;
    const earlyMilestones = stringList(raw.earlyMilestones);
    const explainedPowerups = stringList(raw.explainedPowerups);
    // Module 101 (WP-101.1) — named here or the validator drops them on load.
    const howToSeen = stringList(raw.howToSeen);
    // Module 131 (WP-131.7) — named here or the validator drops it on load.
    const seenFeatureIntros = stringList(raw.seenFeatureIntros);
    const howToRewarded = boolean(raw.howToRewarded);
    // Module 101 (WP-101.10) — `howToSeenUnlocked` (WP-101.6) is gone with the
    // lock idea. It is deliberately NOT named here: an old save carrying it has
    // it dropped on load, which is harmless (nothing reads it any more).
    const poweredBoardsSeen = typeof raw.poweredBoardsSeen === 'number' &&
        Number.isInteger(raw.poweredBoardsSeen) &&
        raw.poweredBoardsSeen >= 0
        ? raw.poweredBoardsSeen
        : undefined;
    const daily = dailyProgress(raw.daily);
    const routeCache = routeCacheEntries(raw.routeCache);
    // Module 118 (WP-118.2) — named here or the validator drops them on load.
    const dailyLogin = dailyLoginState(raw.dailyLogin);
    const quests = questState(raw.quests);
    // Module 132 (WP-132.1) — same: named here or dropped on load (and re-seeded).
    const milestones = milestoneState(raw.milestones);
    // Module 133 (WP-133.1) — same: named here or dropped on load (and redrawn,
    // which a new salt would make a different set).
    const missions = missionStateFromRaw(raw.missions);
    const winStreak = winStreakFromRaw(raw.winStreak);
    const bonusFreeUses = typeof raw.bonusFreeUses === 'number' &&
        Number.isInteger(raw.bonusFreeUses) &&
        raw.bonusFreeUses > 0 &&
        raw.bonusFreeUses <= 1000
        ? raw.bonusFreeUses
        : undefined;
    // Module 126 (WP-126.2) — named here or the validator drops it on load,
    // which would put every player back on Sunny and lose what they got.
    const avatar = avatarStateFromRaw(raw.avatar);
    // Module 152 (WP-152.3) — named here or dropped on load.
    const avatarGoal = typeof raw.avatarGoal === 'string' && AVATAR_ID_PATTERN.test(raw.avatarGoal)
        ? raw.avatarGoal
        : undefined;
    const lastSeenAvatarWave = typeof raw.lastSeenAvatarWave === 'number' &&
        Number.isInteger(raw.lastSeenAvatarWave) &&
        raw.lastSeenAvatarWave >= 1 &&
        raw.lastSeenAvatarWave <= 99
        ? raw.lastSeenAvatarWave
        : undefined;
    // Module 126 (D14, D7) — named here or dropped on load (a lost `welcomeGift`
    // would pay the gift twice).
    const welcomeGift = boolean(raw.welcomeGift);
    const seenSignInNudge = boolean(raw.seenSignInNudge);
    // Module 144 (WP-144.1) — the confirm-card press counts, repaired like
    // `powerupFreeUsed`: sane entries kept, bad ones dropped, the save never
    // rejected. A dropped entry only shows a card's longer form once more.
    let powerupConfirms;
    if (isPlainObject(raw.powerupConfirms)) {
        powerupConfirms = {};
        for (const [id, n] of Object.entries(raw.powerupConfirms)) {
            if (typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 1000)
                powerupConfirms[id] = n;
        }
    }
    return {
        currentTrack,
        tracks,
        boardState: raw.boardState,
        hintsUsed: typeof raw.hintsUsed === 'number' && Number.isFinite(raw.hintsUsed) ? raw.hintsUsed : 0,
        ...(seenUnlocks ? { seenUnlocks } : {}),
        ...(seenTutorial !== undefined ? { seenTutorial } : {}),
        ...(seenTutorials ? { seenTutorials } : {}),
        ...(seenGoalIntro !== undefined ? { seenGoalIntro } : {}),
        ...(seenLanguageIntro !== undefined ? { seenLanguageIntro } : {}),
        ...(seenPoweredHelp !== undefined ? { seenPoweredHelp } : {}),
        ...(seenHomeTour !== undefined ? { seenHomeTour } : {}),
        ...(homeOpened !== undefined ? { homeOpened } : {}),
        ...(seenHomeCoach !== undefined ? { seenHomeCoach } : {}),
        ...(settingsOpened !== undefined ? { settingsOpened } : {}),
        ...(freeCoinsReadyAt !== undefined ? { freeCoinsReadyAt } : {}),
        ...(languageLineAnswered !== undefined ? { languageLineAnswered } : {}),
        ...(earlyMilestones !== undefined ? { earlyMilestones } : {}),
        ...(explainedPowerups ? { explainedPowerups } : {}),
        ...(howToSeen ? { howToSeen } : {}),
        ...(seenFeatureIntros ? { seenFeatureIntros } : {}),
        ...(howToRewarded !== undefined ? { howToRewarded } : {}),
        ...(poweredBoardsSeen !== undefined ? { poweredBoardsSeen } : {}),
        ...(daily !== undefined ? { daily } : {}),
        ...(routeCache ? { routeCache } : {}),
        ...(dailyLogin ? { dailyLogin } : {}),
        ...(quests ? { quests } : {}),
        ...(milestones ? { milestones } : {}),
        ...(missions ? { missions } : {}),
        ...(winStreak ? { winStreak } : {}),
        ...(bonusFreeUses !== undefined ? { bonusFreeUses } : {}),
        ...(powerupConfirms !== undefined ? { powerupConfirms } : {}),
        ...(avatar ? { avatar } : {}),
        ...(avatarGoal !== undefined ? { avatarGoal } : {}),
        ...(lastSeenAvatarWave !== undefined ? { lastSeenAvatarWave } : {}),
        ...(welcomeGift !== undefined ? { welcomeGift } : {}),
        ...(seenSignInNudge !== undefined ? { seenSignInNudge } : {}),
        version: 2,
    };
}
/**
 * Module 87 (WP-87.2b) — rebuilt entry by entry (the Module 81 lesson: a field
 * this function does not name is silently dropped). Anything malformed is
 * skipped rather than failing the save: it is a cache, and `RouteCache.get`
 * replays every hit anyway. Capped here too so a tampered save cannot grow it.
 */
function routeCacheEntries(value) {
    if (!Array.isArray(value))
        return undefined;
    const entries = [];
    for (const item of value) {
        if (Array.isArray(item) &&
            item.length === 2 &&
            typeof item[0] === 'string' &&
            /^[0-9a-f]{8}$/.test(item[0]) &&
            typeof item[1] === 'string' &&
            item[1].length > 0 &&
            item[1].length <= 2000) {
            entries.push([item[0], item[1]]);
        }
    }
    return entries.slice(-ROUTE_CACHE_CAP);
}
/**
 * Module 118 (WP-118.2) — the daily reward's streak, rebuilt field by field.
 * A bad day or date drops the whole record (reads as never claimed: the worst
 * case is a streak restarting at day 1, never a second claim on one day,
 * which the server refuses anyway).
 */
function dailyLoginState(value) {
    if (!isPlainObject(value))
        return undefined;
    const day = value.day;
    if (typeof day !== 'number' || !Number.isInteger(day) || day < 0)
        return undefined;
    if (day > DAILY_LOGIN_SCHEDULE.length)
        return undefined;
    const date = (v) => (typeof v === 'string' && DATE_RE.test(v) ? v : undefined);
    const lastClaimUtc = date(value.lastClaimUtc);
    const repairedUtc = date(value.repairedUtc);
    if (day > 0 && lastClaimUtc === undefined)
        return undefined;
    return {
        day,
        ...(lastClaimUtc !== undefined ? { lastClaimUtc } : {}),
        ...(repairedUtc !== undefined ? { repairedUtc } : {}),
    };
}
/**
 * Module 118 (WP-118.2) — quest state. Unknown quest ids are dropped (a quest
 * removed from `QUESTS` must not linger), progress is clamped to the target.
 */
/**
 * Module 132 (WP-132.1) — repaired, never rejected: unknown ids, bad counts and
 * keys that name no real level are dropped. A dropped counter only starts lower.
 */
function milestoneState(value) {
    if (!isPlainObject(value))
        return undefined;
    const counters = {};
    if (isPlainObject(value.counters)) {
        for (const [id, n] of Object.entries(value.counters)) {
            if (!milestoneDef(id) || typeof n !== 'number' || !Number.isInteger(n) || n <= 0)
                continue;
            counters[id] = Math.min(n, MILESTONE_COUNTER_CAP);
        }
    }
    const claimed = [...new Set((stringList(value.claimed) ?? []).filter(isMilestoneKey))];
    const count = (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0 ? Math.min(n, MILESTONE_COUNTER_CAP) : 0;
    // WP-132.5 — earned dates: keep only real level keys with a real UTC date.
    const earned = {};
    if (isPlainObject(value.earned)) {
        for (const [key, date] of Object.entries(value.earned)) {
            if (isMilestoneKey(key) && typeof date === 'string' && isUtcDate(date))
                earned[key] = date;
        }
    }
    return {
        counters,
        claimed,
        run: count(value.run),
        loginRun: count(value.loginRun),
        ...(Object.keys(earned).length > 0 ? { earned } : {}),
    };
}
/** `YYYY-MM-DD` that is a real calendar day. */
function isUtcDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
function questState(value) {
    if (!isPlainObject(value))
        return undefined;
    const known = new Map(QUESTS.map((q) => [q.id, q.target]));
    const progress = {};
    if (isPlainObject(value.progress)) {
        for (const [id, n] of Object.entries(value.progress)) {
            const target = known.get(id);
            if (target === undefined || typeof n !== 'number' || !Number.isInteger(n) || n <= 0)
                continue;
            progress[id] = Math.min(n, target);
        }
    }
    const claimed = (stringList(value.claimed) ?? []).filter((id) => known.has(id));
    const run = typeof value.run === 'number' && Number.isInteger(value.run) && value.run >= 0
        ? Math.min(value.run, 1000)
        : 0;
    return { progress, claimed, run };
}
